import { LessonProblem, ProblemAnswer } from '@yandex-int/k-common';
import b from 'b_';
import cx from 'classnames';
import { SecondaryKeyboard, Spin } from 'platform-components';
import { MARKER_STATUS_MODE } from 'platform-components/constants';
import { isCodingProblem } from 'platform-components/src/components/coding-problem/utils';
import { isRobotProblem } from 'platform-components/src/components/robot/utils/isRobotProblem';
import { isWebProblem } from 'platform-components/src/components/web-problem/utils';
import { isWebserviceProblem } from 'platform-components/src/components/webservice-problem/utils';
import React, { useCallback } from 'react';

import { ProblemWithLabView } from 'common.components/lab-problem/lab-problem';
import { ProblemRunBar } from 'common.components/problem-run/__bar/problem-run__bar';
import { MarkerAnswer } from 'store/problems-view/typings';
import { isOpenAnswer, isTheory } from 'store/problems/utils';

import './problem-run.scss';

export interface SetMarkerProps {
    markerId: number;
    answer: MarkerAnswer;
    problemId: number;
}

interface Props {
    isAnswered: boolean;
    problemAttempt: ProblemAnswer | null;
    problemLink: LessonProblem | null;
    selectedProblemId: number | null;
    shouldShowProblemId: boolean;
    shouldShowCorrectAnswers: boolean;
    setMarkerAnswer: (payload: SetMarkerProps) => void;
    readonly: boolean;
    mix?: string;
    children: React.ReactNode | Array<React.ReactNode>;
}

// eslint-disable-next-line complexity
export const ProblemRun = (props: Props) => {
    const {
        isAnswered,
        mix,
        problemAttempt,
        problemLink,
        shouldShowProblemId,
        shouldShowCorrectAnswers,
        children,
        readonly,
        setMarkerAnswer,
        selectedProblemId,
    } = props;

    const setAnswer = useCallback(
        ({ markerId, markerAnswer }: { markerId: number; markerAnswer: MarkerAnswer }) => {
            if (selectedProblemId) {
                setMarkerAnswer({ markerId, answer: markerAnswer, problemId: selectedProblemId });
            }
        },
        [setMarkerAnswer, selectedProblemId]
    );

    if (!problemLink || !problemLink.problem) {
        return null;
    }

    // problem может быть не загружен, например, когда восстановили из тоста
    if (!problemLink.problem.markup) {
        return <Spin center />;
    }

    const problem = shouldShowCorrectAnswers
        ? problemLink.problem
        : { ...problemLink.problem, attempt: problemAttempt || undefined };

    const statusMode = isAnswered && !shouldShowCorrectAnswers ? MARKER_STATUS_MODE.WITHOUT_SKIPPED : undefined;

    const readOnlyProblemType =
        isRobotProblem(problemLink.problem) ||
        isWebProblem(problemLink.problem) ||
        isWebserviceProblem(problemLink.problem) ||
        isCodingProblem(problemLink.problem);

    const isReadOnly = readonly || readOnlyProblemType;

    return (
        <div className={cx('problem-run', mix)}>
            <ProblemWithLabView
                mix={b('problem-view', 'problem', {
                    theory: isTheory(problem),
                    openAnswer: isOpenAnswer(problem),
                })}
                previewMode={false}
                problem={problem}
                readOnly={isReadOnly}
                setAnswer={setAnswer}
                shouldShowProblemId={shouldShowProblemId}
                showCorrectAnswers={shouldShowCorrectAnswers}
                showCurrentAnswersStatus={isAnswered}
                statusMode={statusMode}
            />
            {children}

            {!isReadOnly && <SecondaryKeyboard mix="problem-run__secondary-keyboard" />}
        </div>
    );
};

ProblemRun.Bar = ProblemRunBar;
